/**
 * Composable for persisting calendar date and view in URL parameters
 * Simplified version - no filter persistence, only date and view
 * Updated to trigger webpack reload
 */

/**
 * Get current URL search parameters
 * @returns {URLSearchParams}
 */
function getUrlParams() {
	return new URLSearchParams(window.location.search)
}

/**
 * Update URL without causing page reload, preserving all existing parameters
 * @param {Object} updates - Object with key-value pairs to update
 */
function updateUrl(updates) {
	const params = new URLSearchParams(window.location.search)
	
	// Apply updates while preserving existing parameters
	Object.entries(updates).forEach(([key, value]) => {
		if (value === null || value === undefined || value === '') {
			params.delete(key)
		} else {
			params.set(key, value)
		}
	})
	
	const newUrl = `${window.location.pathname}?${params.toString()}`
	window.history.replaceState({}, '', newUrl)
}

export function useUrlFilterPersistence() {
	/**
	 * Save calendar date to URL
	 * @param {Date|string} date - Date to save
	 */
	const saveDateToUrl = (date) => {
		let dateStr = null
		
		if (date) {
			if (date instanceof Date) {
				// Use local date to avoid timezone issues
				const year = date.getFullYear()
				const month = String(date.getMonth() + 1).padStart(2, '0')
				const day = String(date.getDate()).padStart(2, '0')
				dateStr = `${year}-${month}-${day}`
			} else {
				dateStr = date
			}
		}
		
		updateUrl({ date: dateStr })
	}

	/**
	 * Load calendar date from URL
	 * @returns {string|null} - Date string in YYYY-MM-DD format or null
	 */
	const loadDateFromUrl = () => {
		const params = getUrlParams()
		return params.get('date')
	}

	/**
	 * Save calendar view to URL (for single-room mode compatibility)
	 * @param {string} view - Calendar view name
	 */
	const saveViewToUrl = (view) => {
		// View mapping for shorter URL params
		const viewToParam = {
			timeGridDay: 'd',
			timeGridWeek: 'w',
			dayGridMonth: 'm',
			resourceTimeline: 'r'
		}
		
		const viewParam = view && viewToParam[view] ? viewToParam[view] : null
		
		updateUrl({ view: viewParam })
	}

	/**
	 * Load calendar view from URL
	 * @returns {string|null} - Calendar view name or null
	 */
	const loadViewFromUrl = () => {
		const params = getUrlParams()
		const viewParam = params.get('view')
		
		// Param to view mapping
		const paramToView = {
			'd': 'timeGridDay',
			'w': 'timeGridWeek',
			'm': 'dayGridMonth',
			'r': 'resourceTimeline'
		}
		
		return viewParam ? paramToView[viewParam] : null
	}

	/**
	 * Clear date persistence from URL
	 */
	const clearDatePersistence = () => {
		updateUrl({ date: null })
	}

	/**
	 * Check if URL has persisted date
	 * @returns {boolean}
	 */
	const hasPersistedDate = () => {
		const params = getUrlParams()
		return params.has('date')
	}

	return {
		saveDateToUrl,
		loadDateFromUrl,
		saveViewToUrl,
		loadViewFromUrl,
		clearDatePersistence,
		hasPersistedDate
	}
}